import { Fragment, useMemo } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useNavTitle } from '../lib/nav'
import { useQueries, useQuery } from '@tanstack/react-query'
import { leagueToday, gameStatusLabel, teamLabel, positionLabelFor, validateListSearch, type GameRow } from '@region13/shared-types'
import {
  emptyListMessage, listView, useRouteListState, type ListStateProps,
} from '@region13/ui'
import { api, type CoachTeamWithGames, type GameCrew } from '../lib/api'
import { Card } from '../components/ui/Card'
import { TeamScope } from '../components/TeamScope'
import { GameFilterBar } from '../components/GameFilterBar'
import { gameLine } from '../lib/nextGame'
import { COACH_GAMES_LIST, coachGameFacets } from '../lib/coachFacets'
import { SCHEDULE_NOT_PUBLISHED_MESSAGE, REFEREE_CREW_NOT_READY_MESSAGE } from '../lib/scheduleCopy'

export const Route = createFileRoute('/referees')({ component: Referees, validateSearch: validateListSearch })

// CR first, then AR1, AR2, AR3 (the DAO returns them alphabetically; AR3 only
// appears at 6U/7U/8U).
const POS_RANK: Record<string, number> = { CR: 0, AR1: 1, AR2: 2, AR3: 3 }

/** Sign-up state as a pill. `assigneeState` (not the nullable name) is the
 *  filled/open discriminator. Semantic vocabulary, all AA-legible on the
 *  near-white surface: Confirmed = solid green (~5.5:1), Signed up = gold
 *  (~7.2:1), Open = gray outline (~6.8:1). */
function StatusPill({ state }: { state: string | null }) {
  const [cls, label] =
    state === 'confirmed'
      ? ['bg-brand-green-dark text-white', 'Confirmed']
      : state === null
        ? ['border border-line text-ink-soft', 'Open']
        : ['bg-brand-gold/25 text-brand-maroon', 'Signed up']
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{label}</span>
}

/** At-a-glance crew coverage for a game: filled / total — solid green when full,
 *  gold when partial, gray outline when empty. */
function CoverageChip({ filled, total }: { filled: number; total: number }) {
  const cls =
    filled === total ? 'bg-brand-green-dark text-white'
    : filled === 0 ? 'border border-line text-ink-soft'
    : 'bg-brand-gold/25 text-brand-maroon'
  return <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${cls}`}>{filled}/{total} refs</span>
}

/** One game's referee crew: a header line + coverage chip, then a compact
 *  Position · Responsibility · Referee table (ordered CR, AR1, AR2). */
function GameCrewBlock({ crew }: { crew: GameCrew }) {
  // A game that will not be played has no crew to show: its label stands in
  // for the list (the server also releases its referees).
  const status = gameStatusLabel(crew.game.status)
  if (status) {
    return (
      <div className="border-t border-line pt-3">
        <p className="text-sm font-medium text-ink">{gameLine(crew.game, crew.game.opponent)}</p>
        <p className="text-sm text-ink-soft">{status}</p>
      </div>
    )
  }
  const slots = [...crew.slots].sort((a, b) => (POS_RANK[a.position] ?? 9) - (POS_RANK[b.position] ?? 9))
  // Required only: an unfilled optional slot (the second, welcome-not-required
  // official at 6U/7U/8U) is not a coverage gap (spec A4). Counting it would
  // read a fully-covered young game as "2/4 refs" in amber.
  const required = slots.filter((s) => !s.isOptional)
  const filled = required.filter((s) => s.assigneeState !== null).length
  return (
    <div className="border-t border-line pt-3">
      <div className="flex items-baseline justify-between gap-2">
        <p className="min-w-0 text-sm font-medium text-ink">{gameLine(crew.game, crew.game.opponent)}</p>
        <CoverageChip filled={filled} total={required.length} />
      </div>
      {/* ⚠️ THE FIRST TRACK IS max-content, NOT A FIXED WIDTH. It was
          `2.25rem`, which fit the raw enum this page used to print (`CR`,
          `AR1`). Since 2026-09-09 it renders `positionLabelFor`, so the badge
          reads "Referee", "Assistant Referee 1" or "Referee (optional)" — all
          of which overflowed 2.25rem and collided with the responsibility
          column beside it. Sizing the track to the longest badge keeps the
          three columns aligned down the block; `whitespace-nowrap` stops
          "Referee (optional)" wrapping to two lines inside the chip. */}
      <div className="mt-2 grid grid-cols-[max-content_1fr_auto] items-center gap-x-3 gap-y-2 text-sm">
        {slots.map((s, i) => {
          const label = positionLabelFor(s.ageGroup, s.position)
          return (
          <Fragment key={`${s.position}-${i}`}>
            <span className="justify-self-start whitespace-nowrap rounded bg-brand-maroon/10 px-1.5 py-0.5 text-xs font-bold text-brand-maroon">
              {s.isOptional ? `${label} (optional)` : label}
            </span>
            {/* Show the actual responsible team; the coach's own team is
                highlighted in the accent color so it stands out in the crew. */}
            <span className={s.isOwnTeam ? 'min-w-0 truncate font-semibold text-brand-red-dark' : 'min-w-0 truncate text-ink-soft'}>
              {s.responsibility}
            </span>
            <span className="flex items-center justify-end gap-2 text-right">
              {s.assigneeState !== null && (
                // A live assignment can lack an eligibility name — fall back to email.
                <span className="min-w-0 truncate text-ink">{s.assigneeName ?? s.assigneeEmail ?? 'Assigned'}</span>
              )}
              <StatusPill state={s.assigneeState} />
            </span>
          </Fragment>
          )
        })}
      </div>
    </div>
  )
}

/** Loading placeholder — a couple of skeleton game blocks (product register:
 *  skeletons over a mid-content spinner). */
function CrewSkeleton() {
  return (
    <div role="status" aria-label="Loading referee assignments" className="flex flex-col gap-3">
      {[0, 1].map((i) => (
        <div key={i} className="border-t border-line pt-3">
          <div className="h-4 w-2/3 animate-pulse rounded bg-line" />
          <div className="mt-2 flex flex-col gap-2">
            {[0, 1, 2].map((j) => <div key={j} className="h-4 w-full animate-pulse rounded bg-line" />)}
          </div>
        </div>
      ))}
    </div>
  )
}

/** The message shown when this team's referee-crew list is entirely empty
 *  (`all.length === 0`). `teamGamesCount` disambiguates two different states
 *  that collapse into that same empty array: zero means nothing has been
 *  scheduled for ANYONE yet, so `SCHEDULE_NOT_PUBLISHED_MESSAGE` is honest;
 *  non-zero means this team already plays real games and still carries no
 *  referee crew yet — `REFEREE_CREW_NOT_READY_MESSAGE`.
 *
 *  ⚠️ THERE IS NO PERMANENT "TOO YOUNG" CASE ANY MORE. Before the
 *  young-division-referee-slots branch (2026-09-08), 6U/7U/8U never got
 *  referee slots at all, so a real game with no crew was always the age rule —
 *  a permanent fact about that team. Now every division that plays real games
 *  here gets slots (own-team duty for 6U/7U/8U, the Python duty engine for
 *  10U+), so a non-zero `teamGamesCount` with an empty crew is a transient
 *  state (the slot regenerate has not run yet for a schedule change), never a
 *  permanent one. Do not reintroduce an age claim into either message.
 *
 *  `gamesLoading`/`gamesError` cover the one case where `teamGamesCount` is
 *  itself fetched rather than known already: a superadmin viewing as coach,
 *  whose `team.games` is unconditionally `[]` by design (`GET
 *  /api/coach/teams` never inlines games for them — see
 *  coachRoutes.test.ts's "returns ALL season teams" pin, which asserts
 *  exactly that on a team the stub gives a real game). `TeamCrew` fetches the
 *  real count on demand for that case, the same way `cards.tsx`'s `TeamCard`
 *  already does for its own admin-selected games. */
function EmptyCrewMessage({
  teamGamesCount, gamesLoading, gamesError,
}: {
  teamGamesCount: number
  gamesLoading: boolean
  gamesError: boolean
}) {
  if (gamesLoading) return <p role="status" className="text-sm text-ink-soft">Loading…</p>
  if (gamesError) {
    return <p role="alert" className="text-sm text-brand-red-dark">Couldn't load games. Please try again.</p>
  }
  if (teamGamesCount === 0) return <p className="text-sm text-ink-soft">{SCHEDULE_NOT_PUBLISHED_MESSAGE}</p>
  return <p className="text-sm text-ink-soft">{REFEREE_CREW_NOT_READY_MESSAGE}</p>
}

/** A team's games with their referee crews, narrowed by the shared Week and
 *  Field filter and Hide past weeks (on by default). `adminSelected` (from `TeamScope`) is threaded
 *  through only to disambiguate the empty-crew message above; see
 *  `EmptyCrewMessage`'s doc comment. */
export function TeamCrew({ team, adminSelected, listState }: { team: CoachTeamWithGames; adminSelected: boolean; listState: ListStateProps }) {
  const q = useQuery({
    queryKey: ['referee-coverage', team.externalId],
    queryFn: () => api.refereeCoverage(team.externalId),
  })
  const all = useMemo(() => q.data?.games ?? [], [q.data])
  // Each row carries its crew so the filtered rows map straight back to blocks.
  const rows = useMemo(() => all.map((crew) => ({ ...crew.game, division: '', crew }) satisfies GameRow & { crew: GameCrew }), [all])
  const facets = useMemo(() => coachGameFacets(rows), [rows])
  const { visible } = listView({ rows, facets, state: listState.state, today: leagueToday(Date.now()) })
  const games = visible.map((r) => r.crew)

  // Only an admin viewing an empty crew needs the on-demand fetch: a real
  // coach's `team.games` is already inline, and a team with real crew data
  // never reaches EmptyCrewMessage at all. Gating on the exact ambiguous case
  // keeps this from firing for every admin-selected team.
  const needsAdminGames = adminSelected && q.isSuccess && all.length === 0
  const adminGamesQ = useQuery({
    queryKey: ['team-games', team.externalId],
    queryFn: () => api.teamGames(team.externalId),
    enabled: needsAdminGames,
  })
  const teamGamesCount = adminSelected ? (adminGamesQ.data?.games.length ?? 0) : team.games.length

  return (
    <Card as="section">
      <div>
        <h2 className="font-semibold text-ink">
          {teamLabel({ designation: team.externalId, coachName: team.coachName, teamName: team.name })}
        </h2>
        <p className="text-sm text-ink-soft">{team.division}</p>
      </div>
      {q.isLoading ? (
        <CrewSkeleton />
      ) : q.isError ? (
        <p role="alert" className="text-sm text-brand-red-dark">Couldn't load referee assignments. Please try again.</p>
      ) : all.length === 0 ? (
        <EmptyCrewMessage
          teamGamesCount={teamGamesCount}
          gamesLoading={needsAdminGames && adminGamesQ.isLoading}
          gamesError={needsAdminGames && adminGamesQ.isError}
        />
      ) : (
        games.length === 0
          ? <p className="text-sm text-ink-soft">{emptyListMessage(listState)}</p>
          : games.map((gc) => <GameCrewBlock key={gc.game.id} crew={gc} />)
      )}
    </Card>
  )
}

/** Every listed team's crew games, for the screen's one filter bar. Shares the
 *  panels' `referee-coverage` query key, so nothing is fetched twice. */
export function CrewFilterBar({ teams, listState }: { teams: CoachTeamWithGames[]; listState: ListStateProps }) {
  const queries = useQueries({
    queries: teams.map((t) => ({
      queryKey: ['referee-coverage', t.externalId],
      queryFn: () => api.refereeCoverage(t.externalId),
    })),
  })
  const rows = queries.flatMap((q) => q.data?.games ?? []).map((crew): GameRow => ({ ...crew.game, division: '' }))
  return <GameFilterBar rows={rows} listState={listState} />
}

function Referees() {
  const listState = useRouteListState(COACH_GAMES_LIST)
  return (
    <TeamScope
      heading={useNavTitle()}
      emptyText="No teams found for your account this season."
      hint="Pick a team to see its referee assignments."
      bar={(teams) => <CrewFilterBar teams={teams} listState={listState} />}
    >
      {(team, adminSelected) => <TeamCrew team={team} adminSelected={adminSelected} listState={listState} />}
    </TeamScope>
  )
}
